// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Organigrama Interactivo (M1 Structure)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  type Node,
  type Edge,
  type NodeMouseHandler,
  type Connection,
  type EdgeMouseHandler,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { PuestoNode } from '../components/PuestoNode';
import { AppHeader } from '../../../shared/components/AppHeader';
import { ContextMenu, type ContextMenuItem } from '../components/ContextMenu';
import { RelationModal } from '../components/RelationModal';
import { CreatePuestoModal } from '../components/CreatePuestoModal';
import { PersonSelectorModal } from '../components/PersonSelectorModal';
import { EdgePopover } from '../components/EdgePopover';
import { FloatingToolbar } from '../components/FloatingToolbar';
import { C } from '../../../../lib/design';
import { calcularLayout } from '../utils/layoutOrganigrama';
import { useOrganizacion } from '../../../../providers/OrganizacionProvider';
import { fetchTiposRolGobernanza, type TipoRolGobernanzaAdmin } from '../../../shared/services/api';
import { ArtefactoToolbarActions } from '../../../shared/components/ArtefactoToolbarActions';
import { useHasNivelModulo } from '../../../../hooks/useModuloAcceso';
import { PersonaViewerModal } from '../components/PersonaViewerModal';
import { OrganigramaWorkflowBar } from '../components/OrganigramaWorkflowBar';
import {
  fetchOrganigramaArbol,
  fetchOrganigramaZoom,
  fetchPuesto,
  fetchCadenaMando,
  fetchSubordinados,
  actualizarPuesto,
  eliminarPuesto,
  crearRelacion,
  eliminarRelacion,
} from '../../../shared/services/api';

// ─── Constants ───
const NODE_TYPES = { puestoNode: PuestoNode };

const EDGE_STYLES: Record<string, React.CSSProperties> = {
  SOLIDA: { strokeWidth: 2, stroke: C.gray700 },
  PUNTEADA: { strokeWidth: 1.5, stroke: C.gray500, strokeDasharray: '4 4' },
  FUNCIONAL: { strokeWidth: 1.5, stroke: C.gray400, strokeDasharray: '8 4' },
  PROYECTO: { strokeWidth: 1.5, stroke: '#3B82F6', strokeDasharray: '4 4' },
};

// ─── Debounce helper ───
function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback(
    (...args: any[]) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay],
  ) as unknown as T;
}

// ─── Detail Drawer ───
function DetailDrawer({
  puestoId,
  popoverPos,
  onClose,
  onZoom,
  onPersonaClick,
}: {
  puestoId: string;
  popoverPos: { x: number; y: number };
  onClose: () => void;
  onZoom: (id: string) => void;
  onPersonaClick?: (personaId: string) => void;
}) {
  const { t } = useI18n();
  const [puesto, setPuesto] = useState<any>(null);
  const [cadena, setCadena] = useState<any[]>([]);
  const [subordinados, setSubordinados] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchPuesto(puestoId)
        .then((r) => setPuesto(r.data))
        .catch(() => {}),
      fetchCadenaMando(puestoId)
        .then((r) => setCadena(r.data))
        .catch(() => setCadena([])),
      fetchSubordinados(puestoId)
        .then((r) => setSubordinados(r.data))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [puestoId]);

  if (loading) {
    return (
      <div style={getPopoverStyle(popoverPos)}>
        <div style={{ padding: 20, textAlign: 'center' }}>
          <LoadingSpinner text={t.common.cargando} size="sm" />
        </div>
      </div>
    );
  }

  if (!puesto) {
    return (
      <div style={getPopoverStyle(popoverPos)}>
        <div style={{ padding: 20, color: '#DC2626' }}>{t.structure.orgChart.errorLoadPuesto}</div>
        <button onClick={onClose} style={closeBtnStyle}>
          {t.structure.orgChart.close}
        </button>
      </div>
    );
  }

  return (
    <div style={getPopoverStyle(popoverPos)}>
      <style>{`@keyframes fadeInPopover{from{opacity:0;transform:scale(0.96) translateY(-4px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E2E6ED',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-primario)' }}>
            {puesto.titulo}
          </div>
          {puesto.codigo && (
            <div style={{ fontSize: 11, color: C.gray400, marginTop: 2 }}>{puesto.codigo}</div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: C.gray400,
            fontFamily: 'inherit',
          }}
        >
          &#x2715;
        </button>
      </div>

      <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
        {/* Info */}
        <Section title={t.structure.drawer.info}>
          <InfoRow label={t.structure.drawer.nivel} value={puesto.nivel?.replace(/_/g, ' ')} />
          <InfoRow label={t.structure.drawer.departamento} value={puesto.departamento?.nombre} />
          <InfoRow
            label={t.structure.drawer.organizacion}
            value={puesto.organizacion?.nombre ?? puesto.departamento?.organizacion?.nombre}
          />
        </Section>

        {/* Assigned person */}
        <Section title={t.structure.drawer.assignedPerson}>
          {(() => {
            const asig = puesto.asignaciones?.find((a: any) => a.activo);
            const persona = asig?.persona;
            if (!persona)
              return (
                <div style={{ fontSize: 12, color: C.gray400, fontStyle: 'italic' }}>
                  {t.structure.drawer.vacant}
                </div>
              );
            return (
              <>
                <div
                  onClick={() => persona.id && onPersonaClick?.(persona.id)}
                  style={{
                    fontSize: 12,
                    color: 'var(--color-secundario, #33BCE1)',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                >
                  {persona.nombre} {persona.apellidos}
                </div>
                {persona.id && onPersonaClick && (
                  <button
                    onClick={() => onPersonaClick(persona.id)}
                    style={{
                      marginTop: 6,
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      border: 'none',
                      borderRadius: 4,
                      background: 'transparent',
                      color: 'var(--color-acento, #33BCE1)',
                      cursor: 'pointer',
                    }}
                  >
                    {t.structure.orgChart.viewPersonProfile ?? 'Ver ficha completa'} →
                  </button>
                )}
              </>
            );
          })()}
        </Section>

        {/* Chain of command */}
        {cadena.length > 0 && (
          <Section title={t.structure.drawer.chainOfCommand}>
            {cadena.map((item: any, i: number) => (
              <div
                key={i}
                style={{ fontSize: 11, color: C.textSec, paddingLeft: i * 12, marginBottom: 3 }}
              >
                {i > 0 && <span style={{ color: '#D1D5DB' }}>&#x2514; </span>}
                {item.titulo ?? item.puesto?.titulo ?? '\u2014'}
              </div>
            ))}
          </Section>
        )}

        {/* Subordinates */}
        {subordinados?.hijos?.length > 0 && (
          <Section title={`${t.structure.drawer.subordinates} (${subordinados.hijos.length})`}>
            {subordinados.hijos.slice(0, 10).map((sub: any) => (
              <div
                key={sub.id ?? sub.puestoId}
                style={{ fontSize: 11, color: C.textSec, marginBottom: 3 }}
              >
                {sub.titulo ?? '\u2014'}
                {sub.persona && <span style={{ color: C.gray400 }}> ({sub.persona.nombre})</span>}
              </div>
            ))}
            {subordinados.hijos.length > 10 && (
              <div style={{ fontSize: 10, color: C.gray400, marginTop: 4 }}>
                {t.structure.drawer.moreItems(subordinados.hijos.length - 10)}
              </div>
            )}
          </Section>
        )}

        {/* Governance roles */}
        {puesto.rolesGobernanza?.length > 0 && (
          <Section title={t.structure.drawer.governanceRoles}>
            {puesto.rolesGobernanza.map((r: any) => (
              <div
                key={r.id ?? r.tipoRol}
                style={{ fontSize: 11, color: C.gray700, marginBottom: 3 }}
              >
                {r.tipoRol?.replace(/_/g, ' ')}
                {r.ambito && <span style={{ color: C.gray400 }}> \u2014 {r.ambito}</span>}
              </div>
            ))}
          </Section>
        )}

        {/* Zoom button */}
        <button
          onClick={() => onZoom(puestoId)}
          style={{
            width: '100%',
            padding: '8px 0',
            marginTop: 16,
            border: '1px solid var(--color-acento)',
            borderRadius: 6,
            background: 'transparent',
            color: 'var(--color-acento)',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {t.structure.orgChart.semanticZoom}
        </button>
      </div>
    </div>
  );
}

function getPopoverStyle(pos: { x: number; y: number }): React.CSSProperties {
  return {
    position: 'absolute',
    left: pos.x,
    top: pos.y,
    width: 360,
    maxHeight: 520,
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
    border: '1px solid #E2E6ED',
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeInPopover 0.15s ease',
    overflow: 'hidden',
  };
}

const closeBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  margin: 16,
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.text,
          marginBottom: 6,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: C.textSec }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{value ?? '\u2014'}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 0634 — Paleta de colores determinística para roles de gobernanza
// ═══════════════════════════════════════════════════════════════

// Colores legacy hardcoded (preservar para los roles "conocidos" que ya tenían
// color asignado en versiones anteriores — evita ruptura visual).
const COLOR_LEGACY_ROLES: Record<string, string> = {
  DPO: '#1D4ED8',
  OFICIAL_CUMPLIMIENTO: '#16A34A',
  PROPIETARIO_RIESGO: '#DC2626',
  AUDITOR_INTERNO: '#7C3AED',
  RESPONSABLE_CALIDAD: '#C2410C',
  PROPIETARIO_PROCESO: '#33BCE1',
  MIEMBRO_CONSEJO: '#B45309',
};

// Paleta corporativa de 16 colores para roles "nuevos" (ANALISTA_CUMPLIMIENTO,
// SECRETARIO_CONSEJO, CUSTOM_*, etc.). Orden estable → mismo código siempre el
// mismo color.
const PALETA_ROLES_GOBERNANZA = [
  '#0F766E', // teal
  '#9D174D', // pink
  '#92400E', // amber dark
  '#1E3A8A', // azul muy oscuro
  '#5B21B6', // violet
  '#065F46', // emerald
  '#831843', // rose dark
  '#374151', // gray
  '#0E7490', // cyan dark
  '#A16207', // ocre
  '#4338CA', // indigo
  '#BE123C', // crimson
  '#15803D', // green
  '#7E22CE', // purple
  '#0369A1', // sky dark
  '#525252', // neutral
];

function hashCodigo(codigo: string): number {
  let h = 0;
  for (let i = 0; i < codigo.length; i++) {
    h = (h * 31 + codigo.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Color determinístico para un rol de gobernanza por su código. */
export function colorParaRolGobernanza(codigo: string | null | undefined): string {
  if (!codigo) return '#6B7280';
  if (COLOR_LEGACY_ROLES[codigo]) return COLOR_LEGACY_ROLES[codigo];
  return PALETA_ROLES_GOBERNANZA[hashCodigo(codigo) % PALETA_ROLES_GOBERNANZA.length];
}

// ═══════════════════════════════════════════════════════════════
// INNER COMPONENT (wrapped in ReactFlowProvider)
// ═══════════════════════════════════════════════════════════════
function OrganigramaInner({
  embedded = false,
  focusPuesto,
}: {
  embedded?: boolean;
  focusPuesto?: string;
}) {
  const { t } = useI18n();
  // 0615k: useNavigate eliminado - click persona ahora abre PersonaViewerModal.
  const { selectedOrgId } = useOrganizacion();
  const { fitView } = useReactFlow();

  const [direction, setDirection] = useState<'TB' | 'LR'>('TB');
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPuesto, setSelectedPuesto] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomPuesto, setZoomPuesto] = useState<string | null>(null);
  const [zoomBreadcrumb, setZoomBreadcrumb] = useState<{ id: string; titulo: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ─── Capa activa (gobernanza/vacantes/niveles 0635 T-2) ───
  const [capaActiva, setCapaActiva] = useState<'ninguna' | 'gobernanza' | 'vacantes' | 'niveles'>(
    'ninguna',
  );

  // ─── 0634: tipos de rol de gobernanza dinámicos desde BD (antes hardcoded) ───
  const [tiposRolGobernanza, setTiposRolGobernanza] = useState<TipoRolGobernanzaAdmin[]>([]);
  useEffect(() => {
    fetchTiposRolGobernanza({ incluirInactivos: false })
      .then((res) => setTiposRolGobernanza(res.data))
      .catch(() => setTiposRolGobernanza([]));
  }, []);

  // ─── 0635 T-3: estado de la versión activa (recibido del WorkflowBar) ───
  // Solo BORRADOR permite editar el organigrama. Otros estados (EN_REVISION,
  // APROBADO, PUBLICADO, RECHAZADO) congelan el botón "Editar organigrama".
  const [versionState, setVersionState] = useState<string | null>(null);

  // ─── 0635 T-1: versión histórica seleccionada del popover (null = vigente) ───
  const [viewingHistoricVersion, setViewingHistoricVersion] = useState<{
    versionId: string;
    numeroEdicion: number;
  } | null>(null);

  // ─── 0635 T-2.x: filtro activo en la leyenda (null = sin filtro) ───
  const [filtroLeyenda, setFiltroLeyenda] = useState<string | null>(null);

  // 0636 helper: ¿este puesto coincide con el filtro activo?
  // - 'gobernanza' → tiene algún rolGobernanza cuyo tipo coincide con el filtro
  // - 'niveles' → su nivelPuesto.slug coincide con el filtro
  // - 'vacantes' → no aplica filtro (la capa solo distingue vacante vs no)
  function nodoCoincideConFiltro(data: any, filtro: string, capa: string): boolean {
    if (!filtro) return true;
    if (capa === 'gobernanza') {
      const roles = (data?.rolesGobernanza ?? data?.roles ?? []) as Array<any>;
      return roles.some(
        (r) => r?.tipoRolDef?.codigo === filtro || r?.codigo === filtro || r?.tipo === filtro,
      );
    }
    if (capa === 'niveles') {
      const slug = data?.nivelPuesto?.slug ?? data?.nivel?.slug ?? data?.nivel;
      return slug === filtro;
    }
    return true;
  }

  // Editable solo si la version activa es BORRADOR (o no hay version aún) Y
  // no estamos viendo histórico.
  const editAllowedByState =
    (versionState === null || versionState === 'BORRADOR') && viewingHistoricVersion === null;

  // 0636: aplicar el filtro de leyenda al visor — atenuar nodos no coincidentes.
  // Cuando filtroLeyenda cambia, recorremos los nodes existentes y aplicamos
  // style.opacity + filter (grayscale) según matching. Sin filtro → restaurar.
  useEffect(() => {
    setNodes((prevNodes) => {
      if (prevNodes.length === 0) return prevNodes;
      if (!filtroLeyenda) {
        // Quitar atenuación
        return prevNodes.map((n) => {
          if (!n.style?.opacity && !n.style?.filter) return n;
          const { opacity: _o, filter: _f, pointerEvents: _p, ...rest } = (n.style ?? {}) as any;
          return { ...n, style: rest };
        });
      }
      return prevNodes.map((n) => {
        const matches = nodoCoincideConFiltro(n.data, filtroLeyenda, capaActiva);
        return {
          ...n,
          style: {
            ...n.style,
            opacity: matches ? 1 : 0.15,
            filter: matches ? 'none' : 'grayscale(1)',
            pointerEvents: matches ? 'auto' : ('none' as const),
            transition: 'opacity 180ms ease, filter 180ms ease',
          },
        };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroLeyenda, capaActiva]);

  // ─── Edit mode state ───
  // 0620 ADR-024 + filosofia CEO: gating por nivel M1 = ADMIN.
  // Solo ADMIN del modulo M1 (incluye TENANT_OWNER + SUPERADMIN_PLATFORM)
  // puede entrar en editMode (drag&drop, crear nodos, conectar puestos).
  // VIEWER + EDITOR + AUDITOR ven el organigrama pero no mutan.
  // Antes (0615c) era EDITOR — el CEO consolido en QA visual que la
  // edicion debe ser ADMIN-only para alinearlo con la filosofia
  // "mutacion = Administracion" + futuro workflow de aprobacion (ADR-025).
  const canEditM1 = useHasNivelModulo('M1', 'ADMIN');
  const [editMode, setEditMode] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  // 0615k: viewer-only modal de persona (al pinchar nodo persona). Sustituye
  // navegacion previa a /structure?tab=administracion (que requeria ADMIN M1).
  const [viewerPersonaId, setViewerPersonaId] = useState<string | null>(null);

  // 0615c: si el usuario pierde el permiso ADMIN (cambio de accesos del
  // admin durante la sesion via refresh del context), forzar editMode=false
  // para evitar que siga modificando el organigrama. Defensa en profundidad
  // ademas del gating del boton.
  useEffect(() => {
    if (!canEditM1 && editMode) setEditMode(false);
  }, [canEditM1, editMode]);

  // Context menu
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string;
    nodeData: any;
  } | null>(null);

  // Relation modal (onConnect)
  const [pendingConnection, setPendingConnection] = useState<{
    source: string;
    target: string;
    sourceName: string;
    targetName: string;
  } | null>(null);

  // Create puesto modal
  const [showCreatePuesto, setShowCreatePuesto] = useState(false);
  const [createPuestoDefaults, setCreatePuestoDefaults] = useState<{
    orgId?: string;
    reportaA?: string;
  }>({});

  // Person selector modal
  const [personSelector, setPersonSelector] = useState<{ puestoId: string; titulo: string } | null>(
    null,
  );

  // Confirm dialog
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    danger?: boolean;
    onConfirm: () => void;
  } | null>(null);

  // Edge popover
  const [edgePopover, setEdgePopover] = useState<{ x: number; y: number; edgeId: string } | null>(
    null,
  );

  // Selected node for floating toolbar
  const [selectedNodeForToolbar, setSelectedNodeForToolbar] = useState<{
    id: string;
    x: number;
    y: number;
    data: any;
  } | null>(null);

  // Load org chart
  const loadChart = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (selectedOrgId) params.organizacionId = selectedOrgId;
      const data = await fetchOrganigramaArbol(params);
      // 0615k: click en persona abre modal viewer-only (no navega a admin).
      const personaClickFn = (pid: string) => setViewerPersonaId(pid);
      const rawNodes: Node[] = (data.nodes ?? []).map((n: any) => {
        const nd = n.data ?? {};
        const personaId = nd.persona?.id ?? null;
        return {
          id: n.id,
          type: 'puestoNode',
          position: n.position ?? { x: 0, y: 0 },
          data: {
            ...nd,
            editMode,
            personaId,
            personaClick: personaId ? personaClickFn : null,
            capaActiva,
          },
        };
      });

      const rawEdges: Edge[] = (data.edges ?? []).map((e: any) => {
        const rel = e.data?.tipoRelacion ?? 'SOLIDA';
        const edgeStyle = EDGE_STYLES[rel] ?? EDGE_STYLES.SOLIDA;
        return {
          id: e.id,
          source: e.source,
          target: e.target,
          type: 'smoothstep',
          style: edgeStyle,
          animated: rel === 'PROYECTO',
          data: e.data,
        };
      });

      const layouted = calcularLayout(rawNodes, rawEdges, { direction });

      // Apply manual positions if available
      const finalNodes = layouted.nodes.map((node) => {
        const d = node.data as any;
        if (d?.metadatos?.posicionManual && d.metadatos.posX != null && d.metadatos.posY != null) {
          return { ...node, position: { x: d.metadatos.posX, y: d.metadatos.posY } };
        }
        return node;
      });

      setNodes(finalNodes);
      setEdges(layouted.edges);
      setZoomPuesto(null);
      setSelectedNodeForToolbar(null);
    } catch (err) {
      console.error('Error loading organigrama:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, direction, editMode, setNodes, setEdges]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  // Focus on a specific puesto (from persona detail → "Ver en organigrama")
  useEffect(() => {
    if (!focusPuesto || loading || nodes.length === 0) return;
    const target = nodes.find((n) => (n.data as any).puestoId === focusPuesto);
    if (!target) return;
    setTimeout(() => {
      fitView({ nodes: [target], padding: 0.5, duration: 500 });
      setSelectedPuesto(focusPuesto);
    }, 300);
  }, [focusPuesto, loading, nodes, fitView]);

  // Update editMode/capaActiva in node data when toggled
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, editMode, capaActiva, highlighted: n.id === highlightedNodeId },
      })),
    );
    if (!editMode) {
      setSelectedNodeForToolbar(null);
      setContextMenu(null);
      setEdgePopover(null);
    }
  }, [editMode, capaActiva, highlightedNodeId, setNodes]);

  // Semantic zoom
  const handleZoom = useCallback(
    async (puestoId: string) => {
      setLoading(true);
      try {
        const data = await fetchOrganigramaZoom(puestoId, 3);
        const rawNodes: Node[] = (data.nodes ?? []).map((n: any) => ({
          id: n.id,
          type: 'puestoNode',
          position: n.position ?? { x: 0, y: 0 },
          data: { ...n.data, editMode, capaActiva },
        }));
        const rawEdges: Edge[] = (data.edges ?? []).map((e: any) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          type: 'smoothstep',
          style: EDGE_STYLES[e.data?.tipoRelacion ?? 'SOLIDA'] ?? EDGE_STYLES.SOLIDA,
          data: e.data,
        }));
        const layouted = calcularLayout(rawNodes, rawEdges, { direction });
        setNodes(layouted.nodes);
        setEdges(layouted.edges);
        setZoomPuesto(puestoId);
        setSelectedPuesto(null);
        setSelectedNodeForToolbar(null);
        // Build breadcrumb from node data
        const rootNode = (data.nodes ?? [])[0] as any;
        const titulo = rootNode?.data?.titulo ?? puestoId;
        setZoomBreadcrumb((prev) => {
          const existIdx = prev.findIndex((b) => b.id === puestoId);
          if (existIdx >= 0) return prev.slice(0, existIdx + 1);
          return [...prev, { id: puestoId, titulo }];
        });
      } catch (err) {
        console.error('Error loading zoom:', err);
      } finally {
        setLoading(false);
      }
    },
    [direction, editMode, setNodes, setEdges],
  );

  // ─── Node click ───
  const onNodeClick: NodeMouseHandler = useCallback(
    (event, node) => {
      if (editMode) {
        const d = node.data as any;
        setSelectedNodeForToolbar({
          id: node.id,
          x: node.position.x + 110,
          y: node.position.y,
          data: d,
        });
        setSelectedPuesto(null);
        setPopoverPos(null);
      } else {
        const me = event as unknown as MouseEvent;
        const container = containerRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          const MODAL_W = 360;
          const MODAL_H = 480;
          const OFFSET = 12;
          let x = me.clientX - rect.left + OFFSET;
          let y = me.clientY - rect.top;
          // Collision: right edge
          if (x + MODAL_W > rect.width - 8) x = me.clientX - rect.left - MODAL_W - OFFSET;
          // Collision: bottom edge
          if (y + MODAL_H > rect.height - 8) y = rect.height - MODAL_H - 8;
          // Clamp top
          if (y < 8) y = 8;
          setPopoverPos({ x, y });
        }
        setSelectedPuesto((node.data as any).puestoId ?? node.id);
      }
    },
    [editMode],
  );

  // Double-click => semantic zoom (read mode only)
  const onNodeDoubleClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      if (!editMode) {
        const pid = (node.data as any).puestoId ?? node.id;
        handleZoom(pid);
      }
    },
    [editMode, handleZoom],
  );

  // ─── Right-click context menu (edit mode) ───
  const onNodeContextMenu: NodeMouseHandler = useCallback(
    (event, node) => {
      if (!editMode) return;
      event.preventDefault();
      const d = node.data as any;
      setContextMenu({
        x: (event as unknown as MouseEvent).clientX,
        y: (event as unknown as MouseEvent).clientY,
        nodeId: d.puestoId ?? node.id,
        nodeData: d,
      });
    },
    [editMode],
  );

  // ─── Drag stop: save position (debounced) ───
  const savePositionToBackend = useCallback(async (nodeId: string, x: number, y: number) => {
    try {
      // nodeId might be the puestoId from data or the node id
      await actualizarPuesto(nodeId, {
        metadatos: { posX: Math.round(x), posY: Math.round(y), posicionManual: true },
      });
    } catch (err) {
      console.error('Error saving position:', err);
    }
  }, []);

  const debouncedSavePosition = useDebouncedCallback(savePositionToBackend, 500);

  const onNodeDragStop = useCallback(
    (_event: any, node: Node) => {
      if (!editMode) return;
      const puestoId = (node.data as any)?.puestoId ?? node.id;
      debouncedSavePosition(puestoId, node.position.x, node.position.y);
    },
    [editMode, debouncedSavePosition],
  );

  // ─── Reset layout ───
  const resetLayout = useCallback(() => {
    const layouted = calcularLayout(nodes, edges, { direction });
    setNodes(layouted.nodes);
    // Clear manual positions in backend (fire-and-forget)
    nodes.forEach((n) => {
      const d = n.data as any;
      if (d?.metadatos?.posicionManual) {
        const puestoId = d.puestoId ?? n.id;
        actualizarPuesto(puestoId, {
          metadatos: { ...(d.metadatos ?? {}), posicionManual: false },
        }).catch(() => {});
      }
    });
  }, [nodes, edges, direction, setNodes]);

  // ─── onConnect: create relation ───
  const onConnect = useCallback(
    (connection: Connection) => {
      if (!editMode || !connection.source || !connection.target) return;
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);
      setPendingConnection({
        source: (sourceNode?.data as any)?.puestoId ?? connection.source,
        target: (targetNode?.data as any)?.puestoId ?? connection.target,
        sourceName: (sourceNode?.data as any)?.titulo ?? 'Puesto',
        targetName: (targetNode?.data as any)?.titulo ?? 'Puesto',
      });
    },
    [editMode, nodes],
  );

  const handleConfirmRelation = useCallback(
    async (tipoRelacion: string, esPrincipal: boolean) => {
      if (!pendingConnection) return;
      try {
        await crearRelacion({
          puestoPadreId: pendingConnection.source,
          puestoHijoId: pendingConnection.target,
          tipoRelacion,
          esPrincipal,
        });
        setPendingConnection(null);
        loadChart();
      } catch (err) {
        console.error('Error creating relation:', err);
        setPendingConnection(null);
      }
    },
    [pendingConnection, loadChart],
  );

  // ─── Edge click: show popover ───
  const onEdgeClick: EdgeMouseHandler = useCallback(
    (event, edge) => {
      if (!editMode) return;
      setEdgePopover({
        x: (event as unknown as MouseEvent).clientX,
        y: (event as unknown as MouseEvent).clientY,
        edgeId: edge.id,
      });
    },
    [editMode],
  );

  const handleDeleteEdge = useCallback(async () => {
    if (!edgePopover) return;
    try {
      await eliminarRelacion(edgePopover.edgeId);
      setEdgePopover(null);
      loadChart();
    } catch (err) {
      console.error('Error deleting relation:', err);
      setEdgePopover(null);
    }
  }, [edgePopover, loadChart]);

  // ─── Delete puesto ───
  const handleDeletePuesto = useCallback(
    (puestoId: string) => {
      setConfirmState({
        open: true,
        title: t.common.eliminar,
        message: t.structure.confirmDeletePuesto,
        danger: true,
        onConfirm: async () => {
          try {
            await eliminarPuesto(puestoId);
            loadChart();
            setSelectedNodeForToolbar(null);
          } catch (err: unknown) {
            console.error(
              t.structure.errorDeletePuesto + ': ' + (err instanceof Error ? err.message : ''),
            );
          }
        },
      });
    },
    [loadChart, t],
  );

  // ─── Context menu actions ───
  const contextMenuItems: ContextMenuItem[] = useMemo(() => {
    if (!contextMenu) return [];
    return [
      {
        icon: '✏',
        label: t.structure.editPuesto,
        onClick: () => {
          setSelectedPuesto(contextMenu.nodeId);
        },
      },
      {
        icon: '👤',
        label: t.structure.assignPerson,
        onClick: () => {
          setPersonSelector({
            puestoId: contextMenu.nodeId,
            titulo: contextMenu.nodeData?.titulo ?? t.structure.puestoLabel,
          });
        },
      },
      {
        icon: '＋',
        label: t.structure.createSubordinate,
        onClick: () => {
          setCreatePuestoDefaults({ reportaA: contextMenu.nodeId });
          setShowCreatePuesto(true);
        },
      },
      { separator: true },
      {
        icon: '🗑',
        label: t.structure.deletePuesto,
        onClick: () => {
          handleDeletePuesto(contextMenu.nodeId);
        },
      },
    ];
  }, [contextMenu, handleDeletePuesto]);

  // ─── Edit mode toggle ───
  const toggleEditMode = useCallback(() => {
    if (editMode) {
      // Exiting edit mode — reload chart
      setEditMode(false);
      loadChart();
    } else {
      setEditMode(true);
      setShowHelpTooltip(true);
      setTimeout(() => setShowHelpTooltip(false), 5000);
    }
  }, [editMode, loadChart]);

  // ─── Pane click to deselect ───
  const onPaneClick = useCallback(() => {
    setSelectedNodeForToolbar(null);
    setEdgePopover(null);
    if (!editMode) {
      setSelectedPuesto(null);
      setPopoverPos(null);
    }
  }, [editMode]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // 0637: state de loading PNG/PDF eliminado — el componente shared
  // ArtefactoToolbarActions gestiona el loading internamente por botón.
  // setExportingPdf sigue usándose dentro de handleExportPdf para no romper
  // la lógica existente, pero el flag exportingPdf en el JSX ya no se lee.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_exportingPdf, setExportingPdf] = useState(false);
  const handleExportPdf = useCallback(async () => {
    if (!containerRef.current) return;
    setExportingPdf(true);
    try {
      const viewport = containerRef.current.querySelector('.react-flow') as HTMLElement;
      if (!viewport) return;
      const dataUrl = await toPng(viewport, {
        backgroundColor: '#fff',
        pixelRatio: 2,
        filter: (node: HTMLElement) => {
          const className = node.className?.toString() ?? '';
          if (className.includes('react-flow__minimap')) return false;
          if (className.includes('react-flow__controls')) return false;
          return true;
        },
      });
      // Create PDF from captured image
      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
      });
      const orientation = img.width > img.height ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ orientation, unit: 'px', format: [img.width / 2, img.height / 2] });
      pdf.addImage(dataUrl, 'PNG', 0, 0, img.width / 2, img.height / 2);
      pdf.save('organigrama.pdf');
    } catch (err) {
      console.error('Error exporting PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  }, []);

  // Footer stats
  const totalPuestos = nodes.length;
  const vacantes = nodes.filter((n: any) => !n.data?.persona).length;
  const personas = totalPuestos - vacantes;

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--wl-background, #ECEEF2)',
      }}
    >
      {/* ═══ PRESENTATION HEADER ═══ */}
      {!embedded && (
        <AppHeader
          modulo="Structure"
          titulo={t.structure.orgChart.title ?? 'Organigrama Corporativo'}
        />
      )}

      {/* Toolbar (operational sub-header) */}
      <div
        style={{
          background: '#fff',
          borderBottom: '1px solid #E2E6ED',
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* Direction toggle */}
        <div
          style={{
            display: 'flex',
            borderRadius: 6,
            overflow: 'hidden',
            border: '1px solid #D1D5DB',
          }}
        >
          <button
            onClick={() => setDirection('TB')}
            style={{
              padding: '5px 10px',
              border: 'none',
              fontSize: 11,
              fontFamily: 'inherit',
              cursor: 'pointer',
              background: direction === 'TB' ? 'var(--color-primario)' : '#fff',
              color: direction === 'TB' ? '#fff' : C.textSec,
              fontWeight: direction === 'TB' ? 600 : 400,
            }}
          >
            &darr; {t.structure.orgChart.vertical}
          </button>
          <button
            onClick={() => setDirection('LR')}
            style={{
              padding: '5px 10px',
              border: 'none',
              fontSize: 11,
              fontFamily: 'inherit',
              cursor: 'pointer',
              background: direction === 'LR' ? 'var(--color-primario)' : '#fff',
              color: direction === 'LR' ? '#fff' : C.textSec,
              fontWeight: direction === 'LR' ? 600 : 400,
            }}
          >
            &rarr; {t.structure.orgChart.horizontal}
          </button>
        </div>

        {/* Layer selector */}
        <select
          value={capaActiva}
          onChange={(e) => setCapaActiva(e.target.value as any)}
          style={{
            padding: '5px 10px',
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            fontSize: 11,
            fontFamily: 'inherit',
            background: '#fff',
            color: C.gray700,
            cursor: 'pointer',
          }}
        >
          <option value="ninguna">{t.structure.orgChart.layerNone ?? 'Sin capa'}</option>
          <option value="gobernanza">
            {t.structure.orgChart.layerGovernance ?? 'Roles de gobernanza'}
          </option>
          <option value="vacantes">{t.structure.orgChart.layerVacancies ?? 'Vacantes'}</option>
          {/* 0635 T-2: capa Niveles. */}
          <option value="niveles">{t.structure.orgChart.layerLevels ?? 'Niveles'}</option>
        </select>

        {/* Search */}
        <div style={{ position: 'relative', flex: 1, maxWidth: 260 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setHighlightedNodeId(null);
              setSearchNotFound(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const match = nodes.find((n) => {
                  const d = n.data as any;
                  return (
                    d?.titulo?.toLowerCase().includes(q) ||
                    d?.persona?.nombre?.toLowerCase().includes(q) ||
                    d?.persona?.apellidos?.toLowerCase().includes(q) ||
                    `${d?.persona?.nombre ?? ''} ${d?.persona?.apellidos ?? ''}`
                      .toLowerCase()
                      .includes(q)
                  );
                });
                if (match) {
                  fitView({ nodes: [match], padding: 0.5, duration: 500 });
                  setHighlightedNodeId(match.id);
                  setSearchNotFound(false);
                  if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
                  highlightTimerRef.current = setTimeout(() => setHighlightedNodeId(null), 3000);
                } else {
                  setSearchNotFound(true);
                  setTimeout(() => setSearchNotFound(false), 2000);
                }
              }
            }}
            placeholder={t.structure.orgChart.searchPlaceholder}
            style={{
              width: '100%',
              padding: '5px 10px 5px 28px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 11,
              fontFamily: 'inherit',
              outline: 'none',
              background: '#fff',
              color: C.gray700,
            }}
          />
          <svg
            width={13}
            height={13}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {searchNotFound && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 2,
                padding: '3px 8px',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 4,
                fontSize: 10,
                color: '#DC2626',
                zIndex: 20,
              }}
            >
              {t.structure.orgChart.noResults ?? 'Sin resultados'}
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Edit mode toggle / save buttons */}
        {editMode ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {/* Descartar — recarga el layout original desde BD */}
            <button
              onClick={() => {
                setConfirmState({
                  open: true,
                  title: t.structure.orgChart.discardChanges ?? 'Descartar cambios',
                  message:
                    t.structure.orgChart.discardChangesConfirm ??
                    '¿Descartar todos los cambios del organigrama? Esta acción no se puede deshacer.',
                  danger: true,
                  onConfirm: () => {
                    setEditMode(false);
                    loadChart();
                  },
                });
              }}
              style={{ ...toolbarBtnStyle, color: '#DC2626', borderColor: '#FECACA' }}
            >
              {t.structure.orgChart.discardChanges ?? 'Descartar'}
            </button>
            {/* Cerrar sin guardar — sale del modo edición sin recargar */}
            <button onClick={() => setEditMode(false)} style={toolbarBtnStyle}>
              {t.structure.orgChart.closeEditMode ?? 'Cerrar'}
            </button>
            {/* Guardar y salir */}
            <button
              onClick={toggleEditMode}
              style={{
                ...toolbarBtnStyle,
                background: 'var(--color-acento, #33BCE1)',
                color: '#fff',
                fontWeight: 700,
                borderColor: 'var(--color-acento, #33BCE1)',
              }}
            >
              {t.structure.orgChart.saveAndExit}
            </button>
          </div>
        ) : (
          // 0615c ADR-024: solo EDITOR/ADMIN del modulo M1 ven el boton
          // "Editar organigrama". VIEWER no puede entrar en modo edicion.
          // 0635 T-3: además, "Editar" solo si versión activa = BORRADOR (o sin
          // versión aún) Y no se está viendo histórico — evita corromper estados
          // EN_REVISION/APROBADO/PUBLICADO.
          canEditM1 && (
            <>
              {/* 0651 (item BACKLOG [0615] L82): boton siempre visible cuando canEditM1 — disabled
                  con tooltip i18n si versionState ≠ BORRADOR (mejor UX que ocultar silenciosamente). */}
              <button
                onClick={editAllowedByState ? toggleEditMode : undefined}
                disabled={!editAllowedByState}
                style={{
                  ...toolbarBtnStyle,
                  opacity: editAllowedByState ? 1 : 0.5,
                  cursor: editAllowedByState ? 'pointer' : 'not-allowed',
                }}
                title={
                  editAllowedByState
                    ? t.structure.orgChart.editOrgChart
                    : t.structure.orgChart.editButtonDisabledTooltip
                }
              >
                {t.structure.orgChart.editOrgChart}
              </button>

              {/* 0628: WorkflowBar unificada (ADR-025) — envío revisión/aprobar/publicar
                  0635 T-3: callback emite estado al padre.
                  0635 T-1: callback emite version histórica seleccionada. */}
              <OrganigramaWorkflowBar
                enabled
                onVersionStateChange={setVersionState}
                onSelectVersion={(versionId, numeroEdicion) =>
                  setViewingHistoricVersion({ versionId, numeroEdicion })
                }
              />
            </>
          )
        )}

        {/* 0638: 3 acciones canónicas (PNG / PDF / Pantalla completa) al FINAL
            del toolbar — siempre visibles para todo rol (incluso VIEWER que no
            ve el botón Editar ni el WorkflowBar). Mismo componente shared en
            docs y flujogramas → consistencia cross-artefacto. */}
        <ArtefactoToolbarActions
          onExportPng={async () => {
            if (!containerRef.current) return;
            const viewport = containerRef.current.querySelector('.react-flow') as HTMLElement;
            if (!viewport) return;
            const dataUrl = await toPng(viewport, {
              backgroundColor: '#fff',
              pixelRatio: 2,
              filter: (node: HTMLElement) => {
                const cn = node.className?.toString() ?? '';
                return !cn.includes('react-flow__minimap') && !cn.includes('react-flow__controls');
              },
            });
            const link = document.createElement('a');
            link.download = `organigrama_VALOR-IA_${new Date().toISOString().slice(0, 10)}.png`;
            link.href = dataUrl;
            link.click();
          }}
          onExportPdf={handleExportPdf}
          onFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          labels={{
            png: 'PNG',
            pdf: t.structure.orgChart.exportPdf,
            fullscreen: t.structure.orgChart.fullscreen,
            exitFullscreen: t.structure.orgChart.exitFullscreen,
            exporting: t.structure.orgChart.exporting,
          }}
        />
      </div>

      {/* 0635 T-1: banner sticky cuando se está viendo una versión histórica. */}
      {viewingHistoricVersion && (
        <div
          style={{
            background: '#FEF3C7',
            borderTop: '1px solid #F59E0B',
            borderBottom: '1px solid #F59E0B',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: 12,
            color: '#92400E',
            fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <strong>Viendo edición histórica {viewingHistoricVersion.numeroEdicion}</strong>
            <span style={{ color: '#A16207' }}>
              · El visor muestra los puestos vigentes (snapshot histórico
              `STR.versiones_organigrama.snapshotPuestos` pendiente de renderizar — propagado al
              backlog 0636).
            </span>
          </span>
          <button
            type="button"
            onClick={() => setViewingHistoricVersion(null)}
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: '#fff',
              color: '#92400E',
              border: '1px solid #F59E0B',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            title="Volver a la versión vigente"
          >
            Volver a vigente
          </button>
        </div>
      )}

      {/* 0636: banner de filtro eliminado — el toggle se hace re-click en la
          leyenda (la propia leyenda muestra el item resaltado con background
          azul + texto en bold cuando está activo). */}

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Canvas */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            border: editMode ? '2px dashed var(--color-acento, #33BCE1)' : 'none',
          }}
        >
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.7)',
              }}
            >
              <div style={{ fontSize: 14, color: C.textSec }}>
                {t.structure.orgChart.loadingOrgChart}
              </div>
            </div>
          )}

          {/* Edit mode badge */}
          {editMode && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 5,
                padding: '4px 12px',
                borderRadius: 6,
                background: '#F97316',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {t.structure.orgChart.editMode}
            </div>
          )}

          {/* Help tooltip */}
          {editMode && showHelpTooltip && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 100,
                zIndex: 5,
                padding: '8px 14px',
                borderRadius: 8,
                background: '#1F2937',
                color: '#fff',
                fontSize: 11,
                lineHeight: 1.5,
                maxWidth: 360,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
            >
              {t.structure.orgChart.editHelp}
              <button
                onClick={() => setShowHelpTooltip(false)}
                style={{
                  marginLeft: 8,
                  background: 'none',
                  border: 'none',
                  color: C.gray400,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'inherit',
                }}
              >
                &times;
              </button>
            </div>
          )}

          {/* Reset layout button (edit mode) */}
          {editMode && (
            <button
              onClick={resetLayout}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 5,
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                background: '#fff',
                color: C.textSec,
                cursor: 'pointer',
                fontSize: 11,
                fontFamily: 'inherit',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {t.structure.orgChart.resetLayout}
            </button>
          )}

          {zoomPuesto && (
            <div
              style={{
                position: 'absolute',
                top: editMode ? 48 : 12,
                left: 12,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 14px',
                borderRadius: 6,
                background: '#fff',
                border: '1px solid #E2E6ED',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
            >
              <button
                onClick={() => {
                  loadChart();
                  setZoomBreadcrumb([]);
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-acento)',
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  padding: 0,
                }}
              >
                {t.structure.orgChart.backToFull}
              </button>
              {zoomBreadcrumb.map((bc, i) => (
                <span key={bc.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: C.gray400 }}>&rsaquo;</span>
                  {i < zoomBreadcrumb.length - 1 ? (
                    <button
                      onClick={() => handleZoom(bc.id)}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-acento)',
                        fontSize: 12,
                        fontFamily: 'inherit',
                        padding: 0,
                      }}
                    >
                      {bc.titulo}
                    </button>
                  ) : (
                    <span style={{ fontWeight: 600, color: C.gray700 }}>{bc.titulo}</span>
                  )}
                </span>
              ))}
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            onNodeContextMenu={onNodeContextMenu}
            onNodeDragStop={onNodeDragStop}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodeTypes={NODE_TYPES}
            nodesDraggable={editMode}
            nodesConnectable={editMode}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Controls position="bottom-left" />
            <MiniMap
              position="bottom-right"
              nodeColor={(n: any) => n.data?.departamentoColor ?? 'var(--color-primario)'}
              maskColor="rgba(240,240,240,0.6)"
              style={{ borderRadius: 8, border: '1px solid #E2E6ED' }}
            />
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1}
              color={editMode ? '#93C5FD' : '#D1D5DB'}
            />
          </ReactFlow>

          {/* Floating toolbar on selected node (edit mode) */}
          {editMode && selectedNodeForToolbar && (
            <FloatingToolbar
              x={selectedNodeForToolbar.x}
              y={selectedNodeForToolbar.y}
              onEdit={() => {
                const pid = selectedNodeForToolbar.data?.puestoId ?? selectedNodeForToolbar.id;
                setSelectedPuesto(pid);
                // posición centrada en pantalla para el panel de edición en modo edit
                const container = containerRef.current;
                if (container) {
                  const rect = container.getBoundingClientRect();
                  setPopoverPos({
                    x: rect.width / 2 - 180,
                    y: rect.height / 2 - 240,
                  });
                }
              }}
              onAssignPerson={() => {
                setPersonSelector({
                  puestoId: selectedNodeForToolbar.data?.puestoId ?? selectedNodeForToolbar.id,
                  titulo: selectedNodeForToolbar.data?.titulo ?? 'Puesto',
                });
              }}
              onCreateSubordinate={() => {
                setCreatePuestoDefaults({
                  reportaA: selectedNodeForToolbar.data?.puestoId ?? selectedNodeForToolbar.id,
                });
                setShowCreatePuesto(true);
              }}
              onDelete={() =>
                handleDeletePuesto(
                  selectedNodeForToolbar.data?.puestoId ?? selectedNodeForToolbar.id,
                )
              }
            />
          )}

          {/* "+ Nuevo puesto" floating button (edit mode) */}
          {editMode && (
            <button
              onClick={() => {
                setCreatePuestoDefaults({});
                setShowCreatePuesto(true);
              }}
              style={{
                position: 'absolute',
                bottom: 80,
                right: 20,
                zIndex: 10,
                padding: '10px 18px',
                borderRadius: 24,
                border: 'none',
                background: 'var(--color-acento, #33BCE1)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'inherit',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                transition: 'transform 0.15s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
              }}
            >
              {t.structure.orgChart.newPuesto}
            </button>
          )}

          {/* 0639: leyenda permanente de niveles eliminada.
              El usuario la pidió fuera por completo — generaba ruido visual.
              La leyenda de niveles sigue disponible cuando se activa la capa
              "niveles" del selector (top-left, clickable como filtro). */}

          {/* Legend for active layer
              0615j fix: movida de bottom-left a top-left para no tapar
              los Controls de zoom de React Flow (bottom-left). */}
          {capaActiva !== 'ninguna' && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 10,
                background: '#fff',
                border: '1px solid #E2E6ED',
                borderRadius: 8,
                padding: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 11,
                maxHeight: 'calc(100% - 24px)',
                overflowY: 'auto',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, color: C.azul1 }}>
                {t.structure.orgChart.legend ?? 'Leyenda'}
              </div>
              {capaActiva === 'gobernanza' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* 0634: leyenda dinámica desde BD (antes 7 roles hardcoded; ahora todos
                      los activos del catálogo `STR.tipos_rol_gobernanza` del tenant). */}
                  {tiposRolGobernanza.length === 0 ? (
                    <span style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: 11 }}>
                      Cargando roles…
                    </span>
                  ) : (
                    tiposRolGobernanza
                      .slice()
                      .sort((a, b) => {
                        // Sistema primero, luego alfabético por nombre.
                        if (a.esSistema !== b.esSistema) return a.esSistema ? -1 : 1;
                        return a.nombre.localeCompare(b.nombre);
                      })
                      .map((r) => {
                        const color = colorParaRolGobernanza(r.codigo);
                        const isFiltered = filtroLeyenda === r.codigo;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setFiltroLeyenda(isFiltered ? null : (r.codigo ?? null))}
                            aria-pressed={isFiltered}
                            title={`${r.nombre} — click para ${isFiltered ? 'quitar' : 'aplicar'} filtro`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 6px',
                              borderRadius: 4,
                              border: 'none',
                              background: isFiltered ? '#EFF6FF' : 'transparent',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              transition: 'background 120ms ease',
                            }}
                            onMouseEnter={(e) => {
                              if (!isFiltered) {
                                (e.currentTarget as HTMLElement).style.background = '#F8FAFC';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isFiltered) {
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                              }
                            }}
                          >
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: 3,
                                border: `2px solid ${color}`,
                                background: isFiltered ? color : 'transparent',
                                display: 'inline-block',
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                color: C.gray700,
                                fontSize: 11,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 220,
                                fontWeight: isFiltered ? 700 : 400,
                              }}
                            >
                              {r.nombre}
                              {r.esSistema && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 9,
                                    fontWeight: 600,
                                    color: '#9CA3AF',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  (sistema)
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })
                  )}
                </div>
              )}
              {capaActiva === 'vacantes' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        border: '2px dashed #DC2626',
                        display: 'inline-block',
                      }}
                    />
                    <span style={{ color: C.gray700 }}>
                      {t.structure.drawer.vacant ?? 'Vacante'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        border: '2px dashed #DC2626',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 8,
                      }}
                    >
                      ⚠
                    </span>
                    <span style={{ color: C.gray700 }}>
                      {t.structure.orgChart.vacantCritical ?? 'Vacante con rol critico'}
                    </span>
                  </div>
                </div>
              )}

              {/* 0635 T-2: leyenda dedicada para capa "niveles" — clickable como filtro. */}
              {capaActiva === 'niveles' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { nivel: 'C_LEVEL', color: '#7C3AED' },
                    { nivel: 'VP', color: '#5B21B6' },
                    { nivel: 'DIRECTOR', color: '#1D4ED8' },
                    { nivel: 'GERENTE', color: '#0369A1' },
                    { nivel: 'RESPONSABLE', color: '#0D9488' },
                    { nivel: 'JEFE', color: '#059669' },
                    { nivel: 'SENIOR', color: '#D97706' },
                    { nivel: 'STAFF', color: '#EA580C' },
                    { nivel: 'JUNIOR', color: '#64748B' },
                    { nivel: 'BECARIO', color: '#94A3B8' },
                  ].map((n) => {
                    const isFiltered = filtroLeyenda === n.nivel;
                    return (
                      <button
                        key={n.nivel}
                        type="button"
                        onClick={() => setFiltroLeyenda(isFiltered ? null : n.nivel)}
                        aria-pressed={isFiltered}
                        title={`${n.nivel.replace(/_/g, ' ')} — click para ${isFiltered ? 'quitar' : 'aplicar'} filtro`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 6px',
                          borderRadius: 4,
                          border: 'none',
                          background: isFiltered ? '#EFF6FF' : 'transparent',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          textAlign: 'left',
                          transition: 'background 120ms ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!isFiltered)
                            (e.currentTarget as HTMLElement).style.background = '#F8FAFC';
                        }}
                        onMouseLeave={(e) => {
                          if (!isFiltered)
                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            background: n.color,
                            display: 'inline-block',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            color: C.gray700,
                            fontSize: 11,
                            fontWeight: isFiltered ? 700 : 400,
                          }}
                        >
                          {n.nivel.replace(/_/g, ' ')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Detail popover — vista lectura O edición de puesto */}
          {selectedPuesto && popoverPos && (
            <DetailDrawer
              puestoId={selectedPuesto}
              popoverPos={popoverPos}
              onClose={() => {
                setSelectedPuesto(null);
                setPopoverPos(null);
              }}
              onZoom={editMode ? () => {} : handleZoom}
              onPersonaClick={
                editMode
                  ? undefined
                  : // 0615k: viewer-only modal, sin navegar fuera del organigrama.
                    (pid) => setViewerPersonaId(pid)
              }
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          background: '#fff',
          borderTop: '1px solid #E2E6ED',
          padding: '8px 24px',
          display: 'flex',
          gap: 24,
          fontSize: 12,
          color: C.textSec,
          flexShrink: 0,
        }}
      >
        <span>
          {t.structure.orgChart.totalPuestos}: <strong>{totalPuestos}</strong>
        </span>
        <span>
          {t.structure.orgChart.vacantes}: <strong style={{ color: '#DC2626' }}>{vacantes}</strong>
        </span>
        <span>
          {t.structure.orgChart.personasAsignadas}:{' '}
          <strong style={{ color: '#16A34A' }}>{personas}</strong>
        </span>
        {editMode &&
          (() => {
            const manual = nodes.filter((n: any) => n.data?.metadatos?.posicionManual).length;
            return (
              <>
                {manual > 0 && (
                  <span style={{ color: C.gray500, fontSize: 11 }}>
                    {manual} posicion{manual > 1 ? 'es' : ''} manual{manual > 1 ? 'es' : ''}
                  </span>
                )}
                <span style={{ marginLeft: 'auto', color: '#F97316', fontWeight: 600 }}>
                  {t.structure.orgChart.editModeActive}
                </span>
              </>
            );
          })()}
      </div>

      {/* ─── Modals / Overlays ─── */}

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenuItems}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Relation modal (from onConnect) */}
      {pendingConnection && (
        <RelationModal
          sourceName={pendingConnection.sourceName}
          targetName={pendingConnection.targetName}
          onConfirm={handleConfirmRelation}
          onCancel={() => setPendingConnection(null)}
        />
      )}

      {/* Create puesto modal */}
      {showCreatePuesto && (
        <CreatePuestoModal
          defaultOrgId={createPuestoDefaults.orgId}
          defaultReportaA={createPuestoDefaults.reportaA}
          onCreated={() => {
            setShowCreatePuesto(false);
            loadChart();
          }}
          onCancel={() => setShowCreatePuesto(false)}
        />
      )}

      {/* Person selector modal */}
      {personSelector && (
        <PersonSelectorModal
          puestoId={personSelector.puestoId}
          puestoTitulo={personSelector.titulo}
          onAssigned={() => {
            setPersonSelector(null);
            loadChart();
          }}
          onCancel={() => setPersonSelector(null)}
        />
      )}

      {/* Edge popover */}
      {edgePopover && (
        <EdgePopover
          x={edgePopover.x}
          y={edgePopover.y}
          onDelete={handleDeleteEdge}
          onCancel={() => setEdgePopover(null)}
        />
      )}

      {confirmState && (
        <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          danger={confirmState.danger}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {/* 0615k: viewer-only modal de persona (al pinchar nodo o handler hijo). */}
      <PersonaViewerModal personaId={viewerPersonaId} onClose={() => setViewerPersonaId(null)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE (wraps with ReactFlowProvider)
// ═══════════════════════════════════════════════════════════════
export function OrganigramaPage({
  embedded = false,
  focusPuesto,
}: {
  embedded?: boolean;
  focusPuesto?: string;
}) {
  return (
    <ReactFlowProvider>
      <OrganigramaInner embedded={embedded} focusPuesto={focusPuesto} />
    </ReactFlowProvider>
  );
}

const toolbarBtnStyle: React.CSSProperties = {
  padding: '5px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  background: '#fff',
  color: C.textSec,
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'inherit',
};
